/**
 * useTypeahead — SPD v5.83 Phase 2 (2026-07-14) — 12-field contract.
 * ─────────────────────────────────────────────────────────────────
 * Debounced predictive-typeahead hook. Given a partial query string,
 * fires `POST /api/search/typeahead` every 300 ms of quiet typing and
 * returns the LLM classifier's full 12-field payload for the caller
 * to render via <PrimitiveByLayoutHint>.
 *
 * SPD v5.82 back-compat aliases (`completions`, `ambiguity_axes`,
 * `intent_confidence`) are preserved on the result object so any
 * un-migrated caller keeps working.
 */
import { useEffect, useRef, useState } from 'react';
import { API, getToken } from '../utils/authClient';
import { getStockResponse } from './typeaheadStock';
import { recordShownChips, getShownChips } from '../lib/chipShownMemory';

const DEBOUNCE_MS = 250;  // was 300ms; 250ms feels tighter but still avoids per-keystroke LLM spam
// IJ-229 · Wave 3b · 900 ms of quiet typing after a Haiku 4.5 hit fires
// a second Claude Sonnet 4.6 call and swaps the payload in place per
// FSD v1.11 REDLINE-16.
const IDLE_PROMOTION_MS = 900;

const EMPTY_RESULT = {
  // v5.83 fields
  query_class:       'unknown',
  corrected_partial: '',
  completions:       [],
  chips:             [],
  chips_style:       'informational_topic',
  layout_hint:       'prose_and_sources',
  source_strategy:   'web_ka',
  freetext_hint:     '',
  intro_line:        '',
  needs_location:    false,
  needs_price_range: false,
  intent_confidence: 0,
  // v5.82 back-compat aliases (keep both — parent readers vary)
  ambiguity_axes:    [],
  query_specificity: 'unknown',
  // meta
  loading:           false,
  source:            'idle', // 'idle' | 'hit' | 'miss' | 'error'
};

export function useTypeahead(partial, userCtx = {}) {
  const [result, setResult] = useState(EMPTY_RESULT);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  // IJ-229 · Wave 3b · idle-promotion refs.
  const upgradeTimerRef = useRef(null);
  const upgradeAbortRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    // Cancel any pending idle-promotion timer + in-flight upgrade
    // request when a new keystroke arrives — the user is still typing.
    if (upgradeTimerRef.current) clearTimeout(upgradeTimerRef.current);
    if (upgradeAbortRef.current) upgradeAbortRef.current.abort();

    const trimmed = (partial || '').trim();
    if (trimmed.length < 3) {
      setResult(EMPTY_RESULT);
      return;
    }

    // SPD v5.83 · Phase 2c (2026-07-14) — INSTANT client-side stock
    // fallback + stale-while-revalidate. Two problems with the previous
    // behaviour that produced the 10-second "nothing on screen" wait
    // the operator hit on "shoes":
    //   1. `setResult(EMPTY_RESULT + loading:true)` wiped visible chips
    //      on every keystroke — user had NOTHING to look at during the
    //      LLM call. Fix: keep previous result visible, mark loading.
    //   2. No client-side head-start — every partial waited on the LLM
    //      even for the top-10 commodity categories. Fix: if the
    //      partial exactly matches a stock head (shoes, phone, laptop
    //      etc.), render the stock response in < 5 ms so the user sees
    //      chips immediately. The LLM call still runs in parallel and
    //      quietly upgrades the payload when it lands.
    const stock = getStockResponse(trimmed);
    if (stock) {
      setResult((prev) => ({
        ...EMPTY_RESULT,
        ...stock,
        corrected_partial: trimmed,
        // v5.82 back-compat aliases
        ambiguity_axes: stock.chips || [],
        query_specificity: 'category',
        loading: true,          // LLM will still upgrade
        source: 'stock',
      }));
    } else {
      // No stock hit — mark loading but KEEP previous chips visible
      // so the dropdown doesn't flash empty between keystrokes.
      setResult((prev) => ({ ...prev, loading: true }));
    }

    timerRef.current = setTimeout(async () => {
      const token = getToken();
      if (!token) return;
      const ac = new AbortController();
      abortRef.current = ac;
      // IJ-228 · latency instrumentation · capture fetch start
      // wall-clock so the frontend can compute first-paint delta
      // against the FSD v1.11 § 8 300 ms perceptual budget.
      const fetchStart = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      try {
        // IJ-239 · Chip variety persistence — inject the list of
        // chip labels the user has already been shown for this
        // partial in this tab so the backend classifier can pick
        // fresh angles.
        const previouslyShownChips = getShownChips(trimmed);
        const outboundCtx = previouslyShownChips.length > 0
          ? { ...userCtx, previously_shown_chips: previouslyShownChips }
          : userCtx;
        const res = await fetch(`${API}/search/typeahead`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ partial: trimmed, user_ctx: outboundCtx }),
          signal: ac.signal,
        });
        if (!res.ok) {
          // On LLM failure, keep whatever we already have (stock or
          // stale previous) so the user never sees a blank dropdown.
          setResult((r) => ({ ...r, loading: false, source: r.source === 'idle' ? 'error' : r.source }));
          return;
        }
        const data = await res.json();
        // IJ-239 · Record the chip labels shown for this partial so
        // the next call for the same partial can request fresh
        // angles. Prefer option_set (rich chips w/ labels) · fall
        // back to the flat chips array.
        try {
          const opts = Array.isArray(data.option_set) ? data.option_set : [];
          const labels = opts.length > 0
            ? opts.map((o) => (o && (o.label || o.name)) || '').filter(Boolean)
            : (Array.isArray(data.chips) ? data.chips.map(String) : []);
          if (labels.length > 0) recordShownChips(trimmed, labels);
        } catch { /* swallow — chip memory is best-effort */ }
        setResult({
          query_class:       data.query_class       || 'unknown',
          corrected_partial: data.corrected_partial || '',
          completions:       data.completions       || [],
          chips:             data.chips             || data.ambiguity_axes || [],
          chips_style:       data.chips_style       || 'informational_topic',
          layout_hint:       data.layout_hint       || 'prose_and_sources',
          source_strategy:   data.source_strategy   || 'web_ka',
          freetext_hint:     data.freetext_hint     || '',
          intro_line:        data.intro_line        || '',
          needs_location:    Boolean(data.needs_location),
          needs_price_range: Boolean(data.needs_price_range),
          intent_confidence: Number(data.intent_confidence) || 0,
          ambiguity_axes:    data.chips             || data.ambiguity_axes || [],
          query_specificity: data.query_specificity || 'unknown',
          loading:           false,
          source:            data.cache === 'hit' ? 'hit' : 'miss',
        });
        // IJ-228 · fire-and-forget first-paint telemetry. Never blocks
        // the user · errors swallowed so a telemetry hiccup cannot
        // surface as a UI error.
        try {
          const nowMs = (typeof performance !== 'undefined' && performance.now)
            ? performance.now()
            : Date.now();
          const firstPaintMs = Math.max(0, Math.round(nowMs - fetchStart));
          fetch(`${API}/telemetry/typeahead-paint`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              partial_len:    trimmed.length,
              first_paint_ms: firstPaintMs,
              cache_source:   data.cache === 'hit' ? 'hit' : 'miss',
              wall_ms:        typeof data.wall_ms === 'number' ? data.wall_ms : null,
              llm_ms:         typeof data.llm_ms === 'number' ? data.llm_ms : null,
            }),
          }).catch(() => { /* swallow · telemetry never blocks user */ });
        } catch (e) { /* swallow · telemetry never blocks user */ }

        // IJ-229 · Wave 3b · 900 ms idle-promotion timer per FSD v1.11
        // REDLINE-16. Skip if the primary response was a hard refusal
        // (no ethical upside to escalating a refused query to Sonnet).
        if (data.query_class !== 'refused') {
          if (upgradeTimerRef.current) clearTimeout(upgradeTimerRef.current);
          upgradeTimerRef.current = setTimeout(async () => {
            const upgradeAc = new AbortController();
            upgradeAbortRef.current = upgradeAc;
            const upgradeStart = (typeof performance !== 'undefined' && performance.now)
              ? performance.now()
              : Date.now();
            try {
              // IJ-239 · Chip variety persistence carried through to
              // the Sonnet 4.6 upgrade path too — same instruction
              // to the classifier · fresh angles please.
              const upgradePrevShown = getShownChips(trimmed);
              const upgradeCtx = upgradePrevShown.length > 0
                ? { ...userCtx, previously_shown_chips: upgradePrevShown }
                : userCtx;
              const uRes = await fetch(`${API}/search/typeahead/upgrade`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ partial: trimmed, user_ctx: upgradeCtx }),
                signal: upgradeAc.signal,
              });
              if (!uRes.ok) return;
              const uData = await uRes.json();
              // IJ-239 · record the upgraded chip set too.
              try {
                const uOpts = Array.isArray(uData.option_set) ? uData.option_set : [];
                const uLabels = uOpts.length > 0
                  ? uOpts.map((o) => (o && (o.label || o.name)) || '').filter(Boolean)
                  : (Array.isArray(uData.chips) ? uData.chips.map(String) : []);
                if (uLabels.length > 0) recordShownChips(trimmed, uLabels);
              } catch { /* swallow */ }
              setResult({
                query_class:       uData.query_class       || 'unknown',
                corrected_partial: uData.corrected_partial || '',
                completions:       uData.completions       || [],
                chips:             uData.chips             || uData.ambiguity_axes || [],
                chips_style:       uData.chips_style       || 'informational_topic',
                layout_hint:       uData.layout_hint       || 'prose_and_sources',
                source_strategy:   uData.source_strategy   || 'web_ka',
                freetext_hint:     uData.freetext_hint     || '',
                intro_line:        uData.intro_line        || '',
                needs_location:    Boolean(uData.needs_location),
                needs_price_range: Boolean(uData.needs_price_range),
                intent_confidence: Number(uData.intent_confidence) || 0,
                ambiguity_axes:    uData.chips             || uData.ambiguity_axes || [],
                query_specificity: uData.query_specificity || 'unknown',
                loading:           false,
                source:            'upgrade',
              });
              try {
                const uNow = (typeof performance !== 'undefined' && performance.now)
                  ? performance.now()
                  : Date.now();
                const upgradePaintMs = Math.max(0, Math.round(uNow - upgradeStart));
                fetch(`${API}/telemetry/typeahead-paint`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    partial_len:    trimmed.length,
                    first_paint_ms: upgradePaintMs,
                    cache_source:   uData.cache === 'hit' ? 'upgrade_hit' : 'upgrade_miss',
                    wall_ms:        typeof uData.wall_ms === 'number' ? uData.wall_ms : null,
                    llm_ms:         typeof uData.llm_ms === 'number' ? uData.llm_ms : null,
                  }),
                }).catch(() => { /* swallow · telemetry never blocks user */ });
              } catch (e) { /* swallow · telemetry never blocks user */ }
            } catch (err) {
              if (err.name === 'AbortError') return;
              // Silent failure — primary Haiku result stays on screen.
            }
          }, IDLE_PROMOTION_MS);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Same as HTTP error path — don't blank the dropdown.
        setResult((r) => ({ ...r, loading: false, source: r.source === 'idle' ? 'error' : r.source }));
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
      // IJ-229 · clean up idle-promotion resources on unmount / re-fire.
      if (upgradeTimerRef.current) clearTimeout(upgradeTimerRef.current);
      if (upgradeAbortRef.current) upgradeAbortRef.current.abort();
    };
  }, [partial, JSON.stringify(userCtx)]);

  return result;
}

export default useTypeahead;
